import { useQueryClient } from '@tanstack/react-query'
import { createContext, useContext } from 'react'
import { globalQueryKeys } from '@opallabs/api'
import { useWorkspace } from '@opallabs/hooks'
import { toast } from '@opallabs/lapidary'
import { useOnAddToBoard } from '../queries/useOnAddToBoard'
import type { OnAddToBoardArgs, SelectionMeta } from '../types'
import { QueryableAddToBoardToast } from './QueryableAddMomentToBoardModal'

const sendToast = (
  selectionMeta: SelectionMeta | undefined,
  workspaceSlug: string
) => {
  if (!selectionMeta) return

  toast(
    (props) => (
      <QueryableAddToBoardToast
        {...props}
        changes={selectionMeta.boards}
        moments={selectionMeta.moments}
        workspaceSlug={workspaceSlug}
      />
    ),
    {
      type: 'success',
      toastId: 'add-to-board-toast',
      closeOnClick: true,
      autoClose: 5000,
    }
  )
}

type AddToPlanModalContextValue = (args: OnAddToBoardArgs) => void

const QueryableAddToBoardModalContext = createContext<
  AddToPlanModalContextValue | undefined
>(undefined)

export const QueryableAddToBoardModalProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const queryClient = useQueryClient()
  const { slug, uuid: workspaceUuid } = useWorkspace()
  const onAddToBoard = useOnAddToBoard({
    onSuccess: (data, selectionMeta) => {
      queryClient.invalidateQueries(
        globalQueryKeys.momentsByWorkspace(workspaceUuid)
      )
      sendToast(selectionMeta, slug)
    },
  })

  return (
    <QueryableAddToBoardModalContext.Provider value={onAddToBoard}>
      {children}
    </QueryableAddToBoardModalContext.Provider>
  )
}

export const useQueryableAddBoardModal = () =>
  useContext(QueryableAddToBoardModalContext)
